// Anti-scraping: решение о том, идти ли за свежим client_token после 403, и сам
// рекавери.
//
// Инцидент 2026-08-24. Сотрудник грузил аватарки машин, один файл оказался SVG —
// Rack::Attack поймал `<svg` правилом "fail2ban xss payload" (maxretry 1) и забанил
// IP на сутки. За IP сидел офис через NAT, поэтому 403 полетел всем.
//
// Дальше сработала петля в самом фронте. Интерцептор на 403 идёт за новым
// client_token на /api/v1/app_version, и флаг _retried ставит на config ИСХОДНОГО
// запроса — а рекавери уходит с чистым config. Пока app_version отвечает 200, это
// незаметно. Но blocklist отдаёт 403 на ЛЮБОЙ путь забаненного IP, включая сам
// app_version (из throttle-правил он выведен, из blocklist — нет): его 403 приходил
// в тот же интерцептор, где _retried не выставлен, и порождал следующий рекавери.
// Рекурсия без дна — 39 646 запросов за сутки, ~2,7 rps на вкладку до её закрытия.
//
// Поэтому рекавери-запрос исключён: обновлять токен ради запроса, которым мы этот
// токен и получаем, смысла нет — если 403 пришёл на него, дело не в токене.

export const APP_VERSION_PATH = "/api/v1/app_version";

// Метка на config собственного рекавери-запроса. Основной признак — именно она, а
// не разбор URL: она не зависит от baseURL, регистра, query и хоста, и её нельзя
// расшатать, поменяв форму записи пути в точке вызова.
export const TOKEN_RECOVERY_FLAG = "_isTokenRecovery";

// Метка «заголовок этого запроса уже выбран рекавери». Request-интерцептор
// (src/main.js) перезаписывает X-Client-Token значением из localStorage на
// КАЖДОМ запросе, а повтор после рекавери проходит через него же — и без метки
// свежий токен затирался бы обратно протухшим. В обычной жизни это незаметно
// (в localStorage лежит тот же свежий токен), но ровно в том случае, ради
// которого рекавери и переживает бросок setItem — приватный режим Safari,
// переполненное хранилище, — записать туда нечего, и повтор уезжал со старым
// токеном, получал новый 403 и упирался в _retried. Самолечение не работало.
//
// Следствие, которое стоит знать: метка живёт на config и переживает mergeConfig,
// поэтому сетевой ретрай повтора (axios-retry) тоже не перечитает хранилище и
// уедет с тем же токеном. Это безопасно — токен только что выдан сервером и
// живёт 30 дней, — но неочевидно.
export const TOKEN_APPLIED_FLAG = "_clientTokenApplied";

// Собственный таймаут рекавери, короче общего 30-секундного
// (Vue.axios.defaults.timeout): app_version выведен из всех throttle-правил и
// обязан отвечать мгновенно, а с single-flight на одном этом запросе висит вся
// пачка параллельных 403 — раньше каждый ждал свой, теперь все ждут общий.
//
// У укорочения есть цена, и она не нулевая. Таймаут рекавери не ретраится
// (shouldRetryRequest отсекает ECONNABORTED/ETIMEDOUT) и взводит глобальный
// баннер «Проблема с сетью», а приложение живёт в том числе на мобильных сетях,
// которые мигают на секунды. Слишком короткий порог превращал бы тихое
// самолечение в видимую ошибку там, где запрос успел бы дойти. Поэтому 10 с, а
// не 5: втрое меньше общего порога (ради разблокировки пачки) и всё ещё с
// запасом над окном ретраев axios-retry (~1 с + ~2 с с джиттером).
export const RECOVERY_TIMEOUT_MS = 10000;

// Простановка X-Client-Token — та же функция, что зовёт request-интерцептор
// main.js. Вынесена сюда не ради красоты: инвариант «повтор уезжает с тем
// токеном, который выбрал рекавери» держится на паре «интерцептор + метка», и
// проверить его можно только сквозь, на настоящем axios. Пока тело
// интерцептора жило в main.js, тест мог сторожить лишь свою же копию.
export function applyClientTokenHeader(config) {
  if (config[TOKEN_APPLIED_FLAG]) return config;
  const token = localStorage.getItem("client_token");
  if (!token) return config;
  // Соседний recoverAfterForbidden от пустых headers защищён, а эта половина
  // пары — экспортируемая и вызывается из интерцептора: голый TypeError оттуда
  // потерял бы запрос целиком.
  if (!config.headers) config.headers = {};
  config.headers["X-Client-Token"] = token;
  return config;
}

// Разбор URL остаётся вторым признаком — ради прямого вызова из AppVersionCheck.vue,
// который метку не ставит.
//
// Query и hash отрезаем до сравнения: без этого чужой путь, у которого app_version
// попал в параметр (`/api/v1/bookings?next=/api/v1/app_version`), опознавался бы как
// рекавери и молча терял бы обновление токена.
//
// Требуем границу после пути — голое вхождение подстроки поймало бы любой будущий
// маршрут, начинающийся с тех же букв (`/api/v1/app_versions_history`).
export function isAppVersionRequest(url) {
  const value = String(url || "")
    .split("#")[0]
    .split("?")[0];

  const at = value.indexOf(APP_VERSION_PATH);
  if (at === -1) return false;

  const next = value.charAt(at + APP_VERSION_PATH.length);
  return next === "" || next === "/";
}

// Пустое тело ответа — единственный признак, по которому клиент отличает
// анти-скрейп-403 от отказа по правам (см. shouldRefreshClientToken).
//
// Бинарные выгрузки (счета и договоры ходят с responseType: "arraybuffer")
// привозят пустое тело буфером нулевой длины, а не пустой строкой. Тип
// проверяем по значению, а не по наличию поля: JSON-тело `{ size: 0 }` —
// обычный объект, а не пустой Blob.
export function isEmptyResponseBody(data) {
  if (data === undefined || data === null) return true;
  if (typeof data === "string") return data.trim() === "";
  if (typeof ArrayBuffer !== "undefined") {
    if (data instanceof ArrayBuffer) return data.byteLength === 0;
    if (ArrayBuffer.isView(data)) return data.byteLength === 0;
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) return data.size === 0;
  return false;
}

// Рекавери положен ТОЛЬКО анти-скрейп-403, а не любому.
//
// Что отдаёт бэкенд (rentalman-api):
//   — анти-скрейп: `head :forbidden` из ApplicationController#verify_client_token,
//     тело ПУСТОЕ;
//   — отказ по правам: контроллеры рендерят тело — `{error: "Forbidden"}`,
//     `{error: "no access"}`, `{error: "forbidden", code: "task_forbidden"}`,
//     JSON-строку `"not_authorized"` и т.п. (63 из 65 мест);
//   — Rack::Attack blocklist: текст "Forbidden\n" (blocklisted_responder).
//
// Без этой проверки отказ по роли тянул поход за токеном и ПОЛНУЮ переотправку
// запроса — включая POST с телом, который сервер уже отверг. И бан из инцидента
// выше тоже заводил рекавери, хотя токен там ни при чём.
//
// Признак был неполным: ensure_staff! в tasks_controller.rb отвечал таким же
// голым `head :forbidden`, и отказ по роли на /tasks клиенту было не отличить
// от протухшего токена — ровно на create/update/destroy, то есть на запросах с
// телом. Закрыто со стороны бэкенда (rentalman-api#502): теперь там рендерится
// {error: "forbidden", code: "staff_required"}, а инвариант «голое
// head :forbidden живёт только в verify_client_token» держит статический guard
// spec/architecture/forbidden_body_marker_spec.rb. До выката api поведение на
// /tasks остаётся прежним — лишний рекавери, упирающийся в _retried; регрессии
// нет в обоих состояниях, порядок выката развязан.
//
// Второе допущение — инфраструктурное: между клиентом и Rails стоят LBaaS и
// kamal-proxy, и оба сейчас тело ответа не подменяют. Если когда-нибудь
// балансировщик или CDN начнёт отдавать на 403 свою страницу, `head :forbidden`
// приедет с телом и рекавери молча выключится целиком. Признак того, что это
// случилось, — исчезнувшие обновления client_token при живом анти-скрейпе.
export function shouldRefreshClientToken(error) {
  if (!error || !error.response || error.response.status !== 403) return false;
  if (!isEmptyResponseBody(error.response.data)) return false;

  const config = error.config;
  if (!config || config._retried) return false;
  if (config[TOKEN_RECOVERY_FLAG]) return false;

  return !isAppVersionRequest(config.url);
}

// Один рекавери на пачку параллельных 403.
//
// Дедупликации не было: каждый 403 заводил СВОЙ GET /api/v1/app_version.
// Загрузка дашборда — это 10–20 параллельных запросов, и при массовом 403
// (ротация секрета ClientTokenService при TTL токена 30 дней, расхождение часов)
// они давали столько же одновременных обращений за токеном. Бьёт это ровно туда,
// куда бить нельзя: app_version намеренно выведен из ВСЕХ throttle-правил
// rack-attack (config/initializers/rack_attack.rb) — то есть единственный
// нелимитированный эндпоинт и получал усиление.
//
// Слот держится ровно на время полёта: как только рекавери завершился (успехом
// или отказом), следующий 403 заводит новый — иначе клиент навсегда залип бы на
// результате первой попытки.
let inFlightRefresh = null;

export function refreshClientToken(fetchToken) {
  if (inFlightRefresh) return inFlightRefresh;

  let source;
  try {
    source = Promise.resolve(fetchToken());
  } catch (error) {
    return Promise.reject(error);
  }

  // Занять слот заново можно только после того, как он освобождён (сверху стоит
  // ранний return), поэтому сверять идентичность не с чем: обнуляет его всегда
  // тот же самый рекавери, который его и занял.
  const started = source.then(
    (token) => {
      inFlightRefresh = null;
      return token;
    },
    (reason) => {
      inFlightRefresh = null;
      throw reason;
    }
  );

  inFlightRefresh = started;
  return started;
}

// Единственное место, где client_token попадает в хранилище. Вынесено не ради
// красоты: бросок setItem (приватный режим Safari, переполненное хранилище)
// иначе рвёт ту цепочку, в которой оказался. В AppVersionCheck.vue он рвал
// сравнение версий и показ force-update диалога — то есть на нативных
// платформах такой пользователь не увидел бы принудительное обновление
// НИКОГДА. Инвариант «запись только здесь» держит guard в спеке.
export function storeClientToken(token) {
  if (!token) return null;
  try {
    localStorage.setItem("client_token", token);
  } catch (error) {
    // Токен уже в руках вызывающего кода — он поедет в заголовке и без
    // хранилища. Ронять из-за неудачной записи весь поток нечего.
  }
  return token;
}

// Сам поход за токеном. Возвращает свежий токен или null, если сервер его не
// прислал: вызывающий код тогда просто переиграет запрос как есть.
export function fetchClientToken(axiosInstance) {
  return axiosInstance
    .get(APP_VERSION_PATH, {
      [TOKEN_RECOVERY_FLAG]: true,
      timeout: RECOVERY_TIMEOUT_MS,
    })
    .then(function (response) {
      return storeClientToken(
        response && response.data && response.data.client_token
      );
    });
}

// Обработка 403, которому положен рекавери: обновить токен и переиграть запрос.
export function recoverAfterForbidden(error, axiosInstance) {
  const config = error.config;
  config._retried = true;
  if (!config.headers) config.headers = {};

  return refreshClientToken(function () {
    return fetchClientToken(axiosInstance);
  }).then(
    function (token) {
      if (token) {
        config.headers["X-Client-Token"] = token;
        config[TOKEN_APPLIED_FLAG] = true;
      }
      return axiosInstance(config);
    },
    // Обработчик отказа висит ТОЛЬКО на рекавери — вторым аргументом .then, а не
    // отдельным .catch в конце цепочки. Прежний .catch ловил и падение
    // переигранного запроса: повтор, вернувший 422 с сообщениями валидации,
    // доезжал до вызывающего кода как исходный 403, и пользователь не видел,
    // что именно не так с формой.
    function (reason) {
      // Наверх уходит исходный 403 — вызывающий код ждёт именно его. Но тогда
      // настоящая причина исчезает совсем: программная ошибка внутри рекавери
      // (TypeError) становится неотличима от честного «сервер не отдал токен».
      //
      // Именно `cause`, а не своё поле: Rollbar разворачивает цепочку причин
      // ТОЛЬКО по nested/cause (node_modules/rollbar/src/errorParser.js:65,
      // browser/transforms.js:51) — произвольное own-свойство он не читает, и
      // AxiosError#toJSON его тоже не сериализует. С собственным именем поля
      // причина продолжала бы теряться, а комментарий утверждал бы обратное.
      error.cause = reason;
      return Promise.reject(error);
    }
  );
}
